import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { IonDatetime, IonicModule, ToastController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import dayjs from 'dayjs';
import { DatetimeHighlight } from '@ionic/core';
import { difference } from 'lodash';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@bling-fe/shared/utils';

export interface DailyReservableEvent {
  start: string;
  end: string;
}

export interface DateRange {
  from: dayjs.Dayjs;
  to: dayjs.Dayjs;
}

const isSameOrAfter = (date1: dayjs.Dayjs, date2: dayjs.Dayjs): boolean =>
  date1.isAfter(date2) || date1.isSame(date2);

const isSameOrBefore = (date1: dayjs.Dayjs, date2: dayjs.Dayjs): boolean =>
  date1.isBefore(date2) || date1.isSame(date2);

const getEoyLimit = (): dayjs.Dayjs => dayjs().add(1, 'year').endOf('year');

@Component({
  selector: 'bl-date-range-selector',
  standalone: true,
  imports: [CommonModule, TranslateModule, IonicModule, FormsModule],
  templateUrl: './ui-date-range-selector.component.html',
  styleUrls: ['./ui-date-range-selector.component.scss']
})
export class UiDateRangeSelectorComponent implements OnInit {
  @Input() dailyReservableEvents: DailyReservableEvent[] = [];
  @Input() maxDuration = 0;
  @Input() minDuration = 0;
  @Input() minGapBetween = 0;
  @Input() reserveDaysInAdvance: number | undefined;
  @Input() currentUserSelection: string[] = [];

  @Output() valueChange = new EventEmitter<DateRange>();

  @ViewChild(IonDatetime) calendar: IonDatetime | undefined;

  minDate: string | undefined = undefined;
  maxDate: string | undefined = undefined;
  disabledConfirm = true;

  selectedDates: DatetimeHighlight[] = [];
  daysWithReservation: DatetimeHighlight[] = [];
  highlightedDates: DatetimeHighlight[] = [];
  singleSideDates: { date: string; type: 'start' | 'end' }[] = [];

  get today() {
    return dayjs().format('YYYY-MM-DD');
  }

  get from() {
    return this.currentUserSelection[0];
  }

  get to() {
    return this.currentUserSelection[1];
  }

  updateHighlightedDates() {
    // NOTE: previously we had a getter for highlightedDates but
    // that caused issues in date picker: when changing
    // the month it sometimes didn't change the month and
    // jumped back to the previous month.
    this.highlightedDates = [
      ...this.daysWithReservation,
      ...this.selectedDates
    ];
  }

  constructor(
    private toastController: ToastController,
    private i18n: TranslateService
  ) {}

  async ngOnInit() {
    this.maxDate = getEoyLimit().format('YYYY-MM-DD');

    if (this.reserveDaysInAdvance != undefined) {
      this.minDate = dayjs()
        .add(this.reserveDaysInAdvance, 'd')
        .format('YYYY-MM-DD');
      this.calculateDateRestrictions();
      await this.handleDateChange();
    }
  }

  confirm() {
    this.valueChange.emit({
      from: dayjs(this.currentUserSelection[0]),
      to: dayjs(this.currentUserSelection[1])
    });
    this.calendar?.confirm(true);
  }

  async handleDateChange(event?: CustomEvent) {
    this.currentUserSelection = this.getSelectionFromEvent(event);

    // Check if we had an invalid range that got deselected (went from 2 to 1)
    const hadInvalidRange = this.selectedDates.some(
      date => date.backgroundColor === 'var(--ion-color-warning)'
    );

    if (hadInvalidRange && this.currentUserSelection.length === 1) {
      this.resetReservationColors();
    }

    if (!this.isMonthYearPickerOpen(event)) {
      this.updateLimits();
    }

    this.selectedDates = this.getHighlightedDates(
      this.currentUserSelection[0],
      this.currentUserSelection[1]
    );
    this.updateHighlightedDates();

    const hasRange =
      this.currentUserSelection[0] && this.currentUserSelection[1];

    if (!hasRange) {
      this.disabledConfirm = true;
    } else {
      this.disabledConfirm = !this.validateSelectedDateRange({
        from: dayjs(this.currentUserSelection[0]),
        to: dayjs(this.currentUserSelection[1])
      });
    }

    // this.calendar?.ionChange.emit();
  }

  getSelectionFromEvent(event?: CustomEvent) {
    const selectedDates = this.getSelectedDates(event);

    let newSelection = [];

    if (selectedDates.length > 2) {
      if (
        this.isDateBetween(selectedDates[2], selectedDates[0], selectedDates[1])
      ) {
        newSelection = [selectedDates[selectedDates.length - 1]];
      } else {
        newSelection = [
          selectedDates[1],
          selectedDates[selectedDates.length - 1]
        ];
      }
      newSelection.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1));
      return newSelection;
    }

    newSelection = selectedDates.sort((a, b) =>
      dayjs(a).isBefore(dayjs(b)) ? -1 : 1
    );

    return newSelection;
  }

  getSelectedDates(event?: CustomEvent) {
    const isMonthYearPickerOpen = this.isMonthYearPickerOpen(event);
    const newValues =
      (event?.detail.value as string[] | undefined | null) ?? [];

    if (isMonthYearPickerOpen) {
      return newValues.slice(-1);
    } else {
      return newValues;
    }
  }

  updateLimits() {
    if (this.currentUserSelection.length === 0) {
      this.maxDate = getEoyLimit().format('YYYY-MM-DD');
      if (this.reserveDaysInAdvance !== undefined) {
        this.minDate = dayjs()
          .add(this.reserveDaysInAdvance, 'd')
          .format('YYYY-MM-DD');
      }
      // Reset reservation colors when range is deselected
      this.resetReservationColors();
      return;
    }

    if (this.maxDuration && this.currentUserSelection.length === 1) {
      const calculatedMaxDate = dayjs(this.currentUserSelection[0]).add(
        this.maxDuration,
        'd'
      );
      const eoyLimit = getEoyLimit();

      this.maxDate = (
        calculatedMaxDate.isBefore(eoyLimit) ? calculatedMaxDate : eoyLimit
      ).format('YYYY-MM-DD');

      this.minDate = dayjs(this.currentUserSelection[0])
        .subtract(this.maxDuration, 'd')
        .format('YYYY-MM-DD');

      if (this.minDate < this.today) {
        this.minDate = this.today;
      }
    }
  }

  isMonthYearPickerOpen(event?: CustomEvent) {
    const target = event?.target as HTMLDivElement | undefined | null;
    return target?.classList.contains('month-year-picker-open');
  }

  getHighlightedDates = (startDate: string, endDate: string) => {
    const dates: DatetimeHighlight[] = [];
    if (!endDate) {
      return dates;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let current = start;

    const hasInvalidDates = this.daysWithReservation.some(dayConfig => {
      const reservationDate = dayjs(dayConfig.date);
      return reservationDate.isAfter(start) && reservationDate.isBefore(end);
    });

    while (current.isBefore(end)) {
      let backgroundColor = 'var(--ion-color-primary-light)';
      let textColor = 'var(--ion-color-contrast)';
      const currentDateStr = current.format('YYYY-MM-DD');

      const existingDayConfig = this.daysWithReservation.find(
        dayConfig => dayConfig.date === currentDateStr
      );

      // If range goes through disabled dates, highlight ALL middle dates as invalid
      if (hasInvalidDates) {
        backgroundColor = 'var(--ion-color-warning)';
        textColor = 'var(--ion-color-contrast)';

        // Also update existing reservation date styling if it exists
        if (existingDayConfig) {
          existingDayConfig.backgroundColor = 'var(--ion-color-warning)';
          existingDayConfig.textColor = 'var(--ion-color-contrast)';
        }
      }

      dates.push({
        date: currentDateStr,
        backgroundColor,
        textColor
      });
      current = current.add(1, 'day');
    }
    return dates;
  };

  isDateBetween = (date: string, start: string, end: string) => {
    return dayjs(date).isAfter(start) && dayjs(date).isBefore(end);
  };

  isDateAvailable = (date: string) => {
    return !this.dailyReservableEvents.some(
      event =>
        dayjs(date).isAfter(event.start) && dayjs(date).isBefore(event.end)
      /* ||
        dayjs(date).isSame(event.start) ||
        dayjs(date).isSame(event.end) */
    );
  };

  private calculateDateRestrictions(): void {
    const sideDates: (string | Date)[] = [];
    let disabledDates: (string | Date)[] = [];
    let reservedStartDates: (string | Date)[] = [];
    let reservedEndDates: (string | Date)[] = [];

    this.dailyReservableEvents?.forEach((event: DailyReservableEvent) => {
      if (dayjs(event.start).isAfter(dayjs().subtract(1, 'day'))) {
        sideDates.push(event.start);
        reservedStartDates.push(event.start);
      }
      if (dayjs(event.end).isAfter(dayjs().subtract(1, 'day'))) {
        sideDates.push(event.end);
        reservedEndDates.push(event.end);
      }
      // get all reserved dates (start dates, end dates, middle days)
      disabledDates = [...disabledDates, ...this.getDaysBetweenDates(event)];
    });

    // side days which been chosen twice should be disabled
    const duplicateSideDates = sideDates.filter(
      (sideDate, index) => index !== sideDates.indexOf(sideDate)
    );

    disabledDates = [...disabledDates, ...duplicateSideDates];

    // remove duplicate side dates from start dates and end dates
    reservedStartDates = difference(reservedStartDates, duplicateSideDates);
    reservedEndDates = difference(reservedEndDates, duplicateSideDates);

    // all middle days should be disabled
    disabledDates.forEach(disabledDate =>
      this.daysWithReservation.push({
        date: dayjs(disabledDate).format('YYYY-MM-DD'),
        textColor: 'var(--ion-color-warning)'
      })
    );

    // and push them into singleSideDates
    reservedStartDates.forEach(date => {
      this.daysWithReservation.push({
        date: dayjs(date).format('YYYY-MM-DD'),
        textColor: 'var(--ion-color-warning)'
      });
      this.singleSideDates.push({
        date: dayjs(date).format('YYYY-MM-DD'),
        type: 'start'
      });
    });

    reservedEndDates.forEach(date => {
      this.daysWithReservation.push({
        date: dayjs(date).format('YYYY-MM-DD'),
        textColor: 'var(--ion-color-warning)'
      });
      this.singleSideDates.push({
        date: dayjs(date).format('YYYY-MM-DD'),
        type: 'end'
      });
    });

    this.updateHighlightedDates();
  }

  private getDaysBetweenDates(reservedDate: DailyReservableEvent) {
    const startDate = dayjs(reservedDate.start);
    const endDate = dayjs(reservedDate.end);
    let now = startDate.add(1, 'day');
    const dates = [];

    if (endDate.diff(startDate, 'day') > 1) {
      while (now.isBefore(endDate)) {
        if (now.isAfter(dayjs().subtract(1, 'day'))) {
          dates.push(now.format('MM/DD/YYYY'));
        }
        now = now.add(1, 'day');
      }
    }
    return dates;
  }

  private resetReservationColors() {
    this.selectedDates = [];
    this.daysWithReservation = [];
    this.updateHighlightedDates();

    // Note: Ionic ion-datetime has  internal caching that ignores
    // standard Angular change detection. We must clear the arrays completely
    // in one tick, then rebuild clean data in the next tick to force the
    // calendar component to break its cache and re-render properly.
    setTimeout(() => {
      this.calculateDateRestrictions();
    }, 0);
  }

  private async showError(
    message: string,
    params?: Record<string, string | number>
  ) {
    const translatedMessage = this.i18n.instant(message, params);
    const toast = await this.toastController.create({
      message: translatedMessage,
      color: 'danger',
      position: 'bottom',
      duration: 4000
    });
    await toast.present();
  }

  private validateSelectedDateRange(dateRange: DateRange): boolean {
    let hasErrors = false;

    if (
      this.maxDuration > 0 &&
      dateRange.to.diff(dateRange.from, 'day') > this.maxDuration
    ) {
      this.showError(
        _('PAGES.ADD_NEW.DAILY_RESERVATION.ERROR.MAX_DURATION_EXCEEDED'),
        { maxDuration: this.maxDuration }
      );
      hasErrors = true;
    }

    if (
      this.minDuration > 0 &&
      dateRange.to.diff(dateRange.from, 'day') < this.minDuration
    ) {
      this.showError(
        _('PAGES.ADD_NEW.DAILY_RESERVATION.ERROR.MIN_DURATION_NOT_MET'),
        { minDuration: this.minDuration }
      );
      hasErrors = true;
    }

    // side date can not be middle date anymore
    // each side date just can be assigned to start date or end date once or less
    for (const singleSideDate of this.singleSideDates) {
      const date = dayjs(singleSideDate.date);
      if (
        isSameOrAfter(date, dateRange.from) &&
        isSameOrBefore(date, dateRange.to) &&
        ((!date.isSame(dateRange.to) && singleSideDate.type === 'start') ||
          (!date.isSame(dateRange.from) && singleSideDate.type === 'end'))
      ) {
        this.showError(
          _('PAGES.ADD_NEW.DAILY_RESERVATION.ERROR.INCLUDE_DISABLED_DATES')
        );
        hasErrors = true;
        break;
      }
    }

    return !hasErrors;
  }
}
